import { useCallback, useEffect, useState } from "react";
import { Api, ApiError, type DriveFolder, type Source } from "../api.js";
import { useI18n } from "../i18n.js";
import { DriveSource } from "./DrivePanel.js";
import { Badge, Section } from "./ui.js";

const KNOWN = ["not_enabled", "held", "bad_link", "cannot_open", "expired", "not_pending", "too_many_attempts", "too_many_pending", "not_found", "folder_not_ready", "already_added", "forbidden", "limit_reached"];

/** Copy to the clipboard; falls back to selecting nothing (the text is shown in full anyway). */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Google Drive, shared-folder way: paste the folder link, share the folder with Nemo's address, create the code file inside it,
 * press Verify, then use the folder as a source. Shown only when the server has folder sharing on.
 */
export function DriveFolders({ api, agentId, sources, refresh, enabled, shareWith, folders, reload }: { api: Api; agentId: string; sources: Source[]; refresh: () => Promise<void> | void; enabled: boolean; shareWith: string | null; folders: DriveFolder[]; reload: () => Promise<void> }) {
  const { t } = useI18n();
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [errorFor, setErrorFor] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState("");

  const fail = useCallback(
    (e: unknown, id: string | null) => {
      const code = e instanceof ApiError ? e.code : "";
      setError(KNOWN.includes(code) ? t(`dfold.err.${code}` as never) : e instanceof ApiError && e.status === 403 ? t("dfold.err.forbidden") : t("dfold.err.other"));
      setErrorFor(id);
    },
    [t],
  );
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(""), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!enabled && folders.length === 0 && sources.length === 0) return null;

  const start = async () => {
    setBusy("start");
    setError(null);
    setNotice(null);
    try {
      await api.startDriveFolder(link.trim());
      setLink("");
      await reload();
    } catch (e) {
      fail(e, null);
    } finally {
      setBusy("");
    }
  };
  const verify = async (f: DriveFolder) => {
    setBusy(f.id);
    setError(null);
    try {
      await api.verifyDriveFolder(f.id);
      setNotice(t("dfold.verified"));
      await reload();
    } catch (e) {
      fail(e, f.id);
      if (e instanceof ApiError && (e.code === "expired" || e.code === "not_pending")) await reload();
    } finally {
      setBusy("");
    }
  };
  const use = async (f: DriveFolder) => {
    setBusy(f.id);
    setError(null);
    try {
      await api.addSource(agentId, { type: "google_drive", registrationId: f.id });
      setNotice(t("dfold.added"));
      await refresh();
    } catch (e) {
      fail(e, f.id);
    } finally {
      setBusy("");
    }
  };
  const forget = async (f: DriveFolder) => {
    setBusy(f.id);
    setError(null);
    try {
      await api.removeDriveFolder(f.id);
      await reload();
    } catch (e) {
      fail(e, f.id);
    } finally {
      setBusy("");
    }
  };
  const copy = async (what: string, text: string) => setCopied((await copyText(text)) ? what : "");
  const usedBy = (f: DriveFolder) => sources.find((s) => (s.spec as { registrationId?: string }).registrationId === f.id);

  return (
    <Section title={t("dfold.title")} help={t("dfold.help")}>
      {notice && <p role="status" className="notice">{notice}</p>}
      {error && !errorFor && <p role="alert" className="field-error">{error}</p>}

      {enabled && (
        <div className="field">
          <label htmlFor="dfold-link">{t("dfold.link")}</label>
          <input id="dfold-link" type="url" inputMode="url" value={link} placeholder="https://drive.google.com/drive/folders/…" onChange={(e) => setLink(e.target.value)} />
          <button className="btn secondary" disabled={busy !== "" || !link.trim()} onClick={() => void start()}>{busy === "start" ? t("dfold.working") : t("dfold.continue")}</button>
        </div>
      )}

      {folders.map((f) => (
        <div key={f.id} className="card" data-testid={`dfold-${f.status}`}>
          {f.status === "pending" ? (
            <>
              <p><strong>{t("dfold.pendingTitle")}</strong> <span className="hint">{f.folderId}</span></p>
              <ol className="steps-list">
                <li>
                  {t("dfold.step1")} <code data-testid="dfold-address">{shareWith}</code>{" "}
                  <button className="btn ghost" onClick={() => void copy(`a-${f.id}`, shareWith ?? "")}>{copied === `a-${f.id}` ? t("dfold.copied") : t("dfold.copyAddress")}</button>
                </li>
                <li>
                  {t("dfold.step2")} <code data-testid="dfold-code">{f.code}</code>{" "}
                  <button className="btn ghost" onClick={() => void copy(`c-${f.id}`, f.code ?? "")}>{copied === `c-${f.id}` ? t("dfold.copied") : t("dfold.copyName")}</button>
                  <span className="hint"> {t("dfold.step2why")}</span>
                </li>
                <li>{t("dfold.step3")}</li>
              </ol>
              {error && errorFor === f.id && <p role="alert" className="field-error">{error}</p>}
              <div className="row">
                <button className="btn primary" disabled={busy !== ""} onClick={() => void verify(f)}>{busy === f.id ? t("dfold.working") : t("dfold.verify")}</button>
                <button className="btn link" disabled={busy !== ""} onClick={() => void forget(f)}>{t("dfold.cancel")}</button>
              </div>
              <p className="hint">{t("dfold.workspace")}</p>
            </>
          ) : (
            <>
              <p>
                <Badge tone="ok">{t("dfold.status.active")}</Badge> <strong>{f.folderName ?? f.folderId}</strong>
              </p>
              {error && errorFor === f.id && <p role="alert" className="field-error">{error}</p>}
              <div className="row">
                {!usedBy(f) && <button className="btn primary" disabled={busy !== ""} onClick={() => void use(f)}>{busy === f.id ? t("dfold.working") : t("dfold.use")}</button>}
                <button className="btn link" disabled={busy !== ""} onClick={() => void forget(f)}>{t("dfold.stop")}</button>
              </div>
              <p className="hint">{t("dfold.stopHint")}</p>
            </>
          )}
        </div>
      ))}

      {sources.length > 0 && (
        <ul className="sources" aria-label={t("dfold.sources")}>
          {sources.map((s) => (
            <DriveSource key={s.id} api={api} agentId={agentId} source={s} syncNow={async (x) => { try { await api.reindexSource(agentId, x.id); await refresh(); } catch (e) { fail(e, null); } }} remove={async (x) => { try { await api.removeSource(agentId, x.id); await refresh(); } catch (e) { fail(e, null); } }} />
          ))}
        </ul>
      )}
      <p className="hint">{t("dfold.privacy")}</p>
    </Section>
  );
}
